import { useState, useCallback, useRef, useEffect } from 'react';
import config from '../../../config';

// Module-level rate limit: max 1 Nominatim request per second
let lastNominatimReq = 0;

export default function usePlaceSearch() {
  const [place, setPlace] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setPlace(value);
    setLat(null);
    setLon(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < config.vedic.placeMinChars) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      // Rate limit: wait if last request was < 1s ago
      const now = Date.now();
      const wait = Math.max(0, 1000 - (now - lastNominatimReq));
      if (wait > 0) await new Promise(r => setTimeout(r, wait));
      lastNominatimReq = Date.now();

      setLoading(true);
      try {
        const url = `${config.vedic.nominatimUrl}?q=${encodeURIComponent(value)}&format=json&limit=${config.vedic.placeLimit}`;
        const res = await fetch(url, {
          headers: { 'User-Agent': config.vedic.nominatimUserAgent },
        });
        const data = await res.json();
        setSuggestions(data.map(item => ({
          label: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
        })));
      } catch {
        setSuggestions([]);
      }
      setLoading(false);
    }, config.vedic.placeDebounceMs);
  }, []);

  const select = useCallback((s) => {
    setPlace(s.label);
    setLat(s.lat);
    setLon(s.lon);
    setSuggestions([]);
  }, []);

  const setCoords = useCallback((newLat, newLon) => {
    setLat(newLat);
    setLon(newLon);
  }, []);

  // Click-outside to dismiss suggestions
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return {
    place, lat, lon, suggestions,
    handleChange, select, setPlace, setCoords,
    wrapperRef, loading,
  };
}
